'use client';

import { SelectOption } from '@repo/ui/components/multiselect/index';
import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Input } from '@repo/ui/components/ui/input';
import { Label } from '@repo/ui/components/ui/label';
import { cn } from '@repo/ui/lib/utils';
import { Calculator } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import ReactSelect from 'react-select';
import { AESTHETIC_INDEX_TAB, AESTHETIC_INDEX_VALUE, HEALTH_INDEX_TAB, HEALTH_INDEX_VALUE, LOCATION_INDEX_TAB } from '../../../../../lib/inventory/constant/beva.const';
import { useCalculateBevaInventaire } from '../../../hooks/use-beva';
import { FormData } from '../../../shared/types';

interface BevaStepProps {
  formData: FormData;
  errors: { [key: string]: string };
  onChange: (fieldName: string, value: any) => void;
  inventaireId?: number;
}

// Convert constants to select options
const HEALTH_OPTIONS = HEALTH_INDEX_TAB.map(item => ({
  value: item.name.toString(),
  label: item.displayName
}));

const AESTHETIC_OPTIONS = AESTHETIC_INDEX_TAB.map(item => ({
  value: item.name.toString(),
  label: item.displayName
}));

const LOCATION_OPTIONS = LOCATION_INDEX_TAB.map(item => ({
  value: item.name.toString(),
  label: item.displayName
}));

// Health matrix structure based on the constants
const HEALTH_MATRIX = {
  rows: ['Vigoureux', 'Vigueur moyenne', 'Peu vigoureux', 'Sans vigueur'],
  cols: ['Sain', 'Malade', 'Dépérissant'],
  cells: [
    // Vigoureux row
    [
      { id: 1, label: 'Sain - Vigoureux', available: true },
      { id: 5, label: 'Malade - Vigoureux', available: true },
      { id: null, label: '', available: false }
    ],
    // Vigueur moyenne row
    [
      { id: 2, label: 'Sain - Vigueur moyenne', available: true },
      { id: 6, label: 'Malade - Vigueur moyenne', available: true },
      { id: null, label: '', available: false }
    ],
    // Peu vigoureux row
    [
      { id: 3, label: 'Sain - Peu vigoureux', available: true },
      { id: 7, label: 'Malade - Peu vigoureux', available: true },
      { id: 11, label: 'Dépérissant - Peu vigoureux', available: true }
    ],
    // Sans vigueur row
    [
      { id: 4, label: 'Sain - Sans vigueur', available: true },
      { id: 8, label: 'Malade - Sans Vigueur', available: true },
      { id: 12, label: 'Dépérissant - Sans vigueur', available: true }
    ]
  ]
};

// Aesthetic matrix structure
const AESTHETIC_MATRIX = {
  rows: ['Solitaire', 'Groupe < 5', 'Alignement ou groupe > 5'],
  cols: ['Remarquable', 'Beau sujet', 'Mal formé, âgé', 'Sans intérêt'],
  cells: [
    // Solitaire row
    [
      { id: 1, label: 'Solitaire - Remarquable', available: true },
      { id: 2, label: 'Solitaire - Beau sujet', available: true },
      { id: 3, label: 'Solitaire - Mal formé, âgé', available: true },
      { id: 4, label: 'Solitaire - Sans intérêt', available: true }
    ],
    // Groupe < 5 row
    [
      { id: 5, label: 'Groupe < 5 - Remarquable', available: true },
      { id: 6, label: 'Groupe < 5 - Beau sujet', available: true },
      { id: 7, label: 'Groupe < 5 - Mal formé, âgé', available: true },
      { id: 8, label: 'Groupe < 5 - Sans intérêt', available: true }
    ],
    // Alignement ou groupe > 5 row
    [
      { id: null, label: '', available: false },
      { id: 10, label: 'Alignement groupe > 5 - Beau sujet', available: true },
      { id: 11, label: 'Alignement groupe > 5 - Mal formé, âgé', available: true },
      { id: 12, label: 'Alignement groupe > 5 - Sans intérêt', available: true }
    ]
  ]
};

interface MatrixProps {
  title: string;
  matrix: typeof HEALTH_MATRIX;
  selectedId: number | string | null;
  onSelect: (id: number) => void;
  error?: string;
}

function Matrix({ title, matrix, selectedId, onSelect, error }: MatrixProps) {
  const getGridColsClass = (colCount: number) => {
    switch (colCount) {
      case 3: return 'grid-cols-3';
      case 4: return 'grid-cols-4';
      default: return 'grid-cols-3';
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        {title} <span className="text-red-500">*</span>
      </Label>
      <div className="border rounded-lg overflow-hidden">
        {/* Header row */}
        <div className="grid grid-cols-[120px_1fr] bg-gray-50">
          <div className="p-2 border-r font-medium text-xs">{title}</div>
          <div className={`grid ${getGridColsClass(matrix.cols.length)} divide-x`}>
            {matrix.cols.map((col, index) => (
              <div key={index} className="p-2 text-center font-medium text-xs">
                {col}
              </div>
            ))}
          </div>
        </div>

        {/* Matrix rows */}
        {matrix.rows.map((row, rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-[120px_1fr] border-t">
            <div className="p-2 border-r font-medium text-xs bg-gray-50">
              {row}
            </div>
            <div className={`grid ${getGridColsClass(matrix.cols.length)} divide-x`}>
              {(matrix.cells[rowIndex] || []).map((cell, colIndex) => (
                <div key={colIndex} className="p-0.5">
                  {cell.available ? (
                    <Button
                      variant={selectedId === cell.id ? "default" : "outline"}
                      className={cn(
                        "w-full h-10 text-[10px] p-1 whitespace-normal leading-tight",
                        selectedId === cell.id
                          ? "bg-green-500 hover:bg-green-600 text-white"
                          : "bg-green-100 hover:bg-green-200 text-green-800 border-green-300"
                      )}
                      onClick={() => cell.id && onSelect(cell.id)}
                    >
                      {/* Empty button for visual consistency */}
                    </Button>
                  ) : (
                    <div className="w-full h-10 bg-gray-100"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}

export function BevaStep({ formData, errors, onChange, inventaireId }: BevaStepProps) {
  const [calculatedBeva, setCalculatedBeva] = useState<number | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const calculateBevaMutation = useCalculateBevaInventaire();

  // Get values from form data using the correct field names that match the backend
  const varietyGrade = formData['valeur-intrinseque-indice-espece'] || '';
  const healthColumn = formData['health_column'] || '';
  const aestheticColumn = formData['aesthetic_column'] || '';
  const locationIndex = formData['location_index'] ? formData['location_index'].toString() : '';

  // Calculate BEVA values based on selected indices
  const calculatedValues = useMemo(() => {
    const variety = parseFloat(varietyGrade) || 0;

    // Find health value from constants based on column
    const healthItem = HEALTH_INDEX_VALUE.find(item => item.column === parseInt(healthColumn));
    const healthIndex = healthItem ? healthItem.value : 0;

    // Find aesthetic value from constants based on column
    const aestheticItem = AESTHETIC_INDEX_VALUE.find(item => item.column === parseInt(aestheticColumn));
    const aestheticIndex = aestheticItem ? aestheticItem.value : 0;

    const location = parseFloat(locationIndex) || 0;

    return {
      varietyGrade: variety,
      healthIndex,
      aestheticIndex,
      locationIndex: location
    };
  }, [varietyGrade, healthColumn, aestheticColumn, locationIndex]);

  // Update form data when calculated values change
  useEffect(() => {
    // Store the column values (what user selected) and calculated indices
    onChange('health_index', calculatedValues.healthIndex);
    onChange('aesthetic_index', calculatedValues.aestheticIndex);
    onChange('location_index', calculatedValues.locationIndex);


  }, [calculatedValues]);

  // Auto-calculate BEVA when component mounts and all required data is available
  useEffect(() => {
    const hasAllRequiredData = varietyGrade && healthColumn && aestheticColumn && locationIndex && inventaireId;

    if (hasAllRequiredData && calculatedBeva === null && !isInitialLoading) {
      setIsInitialLoading(true);
      calculateBevaOnBackend().finally(() => {
        setIsInitialLoading(false);
      });
    }
  }, [varietyGrade, healthColumn, aestheticColumn, locationIndex, inventaireId, calculatedBeva, isInitialLoading]);

  // Function to calculate BEVA on backend
  const calculateBevaOnBackend = async () => {
    if (!varietyGrade || !healthColumn || !aestheticColumn || !locationIndex || !inventaireId) {
      console.warn('Missing required data for BEVA calculation:', {
        varietyGrade,
        healthColumn,
        aestheticColumn,
        locationIndex,
        inventaireId
      });
      return;
    }

    try {
      const bevaData = {
        varietyGrade: parseFloat(varietyGrade),
        healthIndex: calculatedValues.healthIndex,
        aestheticIndex: calculatedValues.aestheticIndex,
        locationIndex: calculatedValues.locationIndex,
        healthColumn: parseInt(healthColumn),
        aestheticColumn: parseInt(aestheticColumn)
      };

      const result = await calculateBevaMutation.mutateAsync({
        inventaireId,
        data: bevaData
      });

      // Set the calculated BEVA value from the backend
      //@ts-ignore
      setCalculatedBeva(result.data.beva);

      // Show success message (you could add toast notification here)
      //@ts-ignore
      console.log('BEVA calculated successfully:', result.data);

    } catch (error) {
      console.error('Error calculating BEVA:', error);
      // You could add error toast notification here
    }
  };

  return (
    <div className="space-y-6">
      {/* Variety Grade (Species Index) */}
      {/* <div className="space-y-2">
        <Label className="text-sm font-medium">
          Note variétale (Indice espèce) <span className="text-red-500">*</span>
        </Label>
        <Input
          type="number"
          step="0.1"
          value={varietyGrade}
          onChange={(e) => onChange('valeur-intrinseque-indice-espece', e.target.value)}
          placeholder="Entrez la note variétale (ex: 1.5)"
          className={errors['valeur-intrinseque-indice-espece'] ? 'border-red-500' : ''}
        />
        {errors['valeur-intrinseque-indice-espece'] && (
          <p className="text-sm text-red-600">{errors['valeur-intrinseque-indice-espece']}</p>
        )}
      </div> */}

      {/* Matrices and Calculated Values - Side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Matrices Column */}
        <div className="space-y-4">
          {/* Health Index Matrix */}
          <Matrix
            title="Indice sanitaire"
            matrix={HEALTH_MATRIX}
            selectedId={healthColumn ? parseInt(healthColumn) : null}
            onSelect={(id) => onChange('health_column', id.toString())}
            error={errors['health_column']}
          />

          {/* Aesthetic Index Matrix */}
          <Matrix
            title="Indice esthétique"
            matrix={AESTHETIC_MATRIX}
            selectedId={aestheticColumn ? parseInt(aestheticColumn) : null}
            onSelect={(id) => onChange('aesthetic_column', id.toString())}
            error={errors['aesthetic_column']}
          />
        </div>

        {/* Calculated Values Column */}
        <Card className="bg-gray-50 h-fit mt-8">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Valeurs calculées</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Location Index Input */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Indice de situation <span className="text-red-500">*</span>
              </Label>
              <ReactSelect<SelectOption<string>, false>
                options={LOCATION_OPTIONS}
                value={LOCATION_OPTIONS.find(opt => opt.value === locationIndex) || null}
                onChange={(newValue) => {
                  const selectedValue = newValue?.value;
                  if (selectedValue) {
                    onChange('location_index', parseFloat(selectedValue));
                  } else {
                    onChange('location_index', null);
                  }
                }}
                placeholder="Sélectionnez l'indice de situation"
                isSearchable={false}
                isClearable={true}
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (base: any) => ({
                    ...base,
                    minHeight: '44px',
                    backgroundColor: '#ffffff',
                    border: errors['location_index']
                      ? '2px solid hsl(var(--destructive))'
                      : '1px solid hsl(var(--input))',
                    boxShadow: errors['location_index']
                      ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                      : 'none',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: errors['location_index']
                        ? 'hsl(var(--destructive))'
                        : 'hsl(var(--input) / 0.7)',
                    },
                  }),
                  input: (base: any) => ({
                    ...base,
                    color: 'rgb(31, 41, 55)',
                  }),
                  placeholder: (base: any) => ({
                    ...base,
                    color: 'hsl(var(--muted-foreground))',
                    fontSize: '14px',
                  }),
                  option: (base: any, state: any) => ({
                    ...base,
                    backgroundColor: state.isSelected
                      ? 'hsl(var(--primary))'
                      : state.isFocused
                        ? 'hsl(var(--primary) / 0.1)'
                        : '#ffffff',
                    color: state.isSelected ? '#ffffff' : 'rgb(31, 41, 55)',
                    cursor: 'pointer',
                    padding: '10px 12px',
                  }),
                  menu: (base: any) => ({
                    ...base,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    borderRadius: '8px',
                    marginTop: '8px',
                  }),
                }}
              />
              {errors['location_index'] && (
                <p className="text-sm text-red-600">{errors['location_index']}</p>
              )}
            </div>

            <div className="border-t pt-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-600">Note variétale</Label>
                  <div className="text-sm font-medium">{calculatedValues.varietyGrade}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Indice sanitaire (valeur)</Label>
                  <div className="text-sm font-medium">{calculatedValues.healthIndex}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Indice esthétique (valeur)</Label>
                  <div className="text-sm font-medium">{calculatedValues.aestheticIndex}</div>
                </div>
                <div>
                  <Label className="text-xs text-gray-600">Indice de situation</Label>
                  <div className="text-sm font-medium">{calculatedValues.locationIndex}</div>
                </div>
              </div>
            </div>

            <div className="pt-3 border-t">
              <div className="flex justify-between items-center">
                <Label className="text-sm font-medium text-gray-700">Valeur BEVA</Label>
                <div className="text-lg font-bold text-green-600">
                  {isInitialLoading || calculateBevaMutation.isPending ?
                    'Calcul en cours...' :
                    calculatedBeva !== null ? `${calculatedBeva.toFixed(2)} €` : 'Non calculée'
                  }
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {calculatedBeva !== null ?
                  'Formule: (Esthétique + Sanitaire) × (Variétale × Situation) × Taille × Nombre' :
                  isInitialLoading || calculateBevaMutation.isPending ?
                    'Calcul automatique en cours...' :
                    'Cliquez sur "Recalculer la valeur BEVA" pour obtenir le résultat'
                }
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calculate BEVA Button */}
      <div className="space-y-2">
        <Button
          onClick={calculateBevaOnBackend}
          disabled={calculateBevaMutation.isPending || isInitialLoading || !varietyGrade || !healthColumn || !aestheticColumn || !locationIndex || !inventaireId}
          className="w-full flex items-center justify-center space-x-2"
        >
          {/* @ts-ignore */}
          <Calculator className="w-4 h-4" />
          <span>
            {calculateBevaMutation.isPending || isInitialLoading ? 'Calcul en cours...' : 'Recalculer la valeur BEVA'}
          </span>
        </Button>
        <p className="text-xs text-gray-500 text-center">
          Le calcul BEVA prend en compte le diamètre de l'arbre pour déterminer l'indice de taille
        </p>
        {calculateBevaMutation.isError && (
          <p className="text-xs text-red-600 text-center">
            Erreur lors du calcul BEVA. Veuillez réessayer.
          </p>
        )}
      </div>


      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4">
          <div className="flex items-start space-x-2">
            <div className="text-blue-600 mt-0.5">ℹ️</div>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Information sur l'évaluation BEVA</p>
              <p>
                La méthode BEVA (Barème d'Évaluation de la Valeur des Arbres) permet d'estimer
                la valeur patrimoniale d'un arbre en tenant compte de son espèce, de son état
                sanitaire, de ses qualités esthétiques et de sa situation géographique.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
